/**
 * Hook para monitorar atividade suspeita
 * Detecta tentativas de manipulação do DevTools, múltiplas falhas de login, etc.
 */

import { useEffect, useCallback, useRef } from 'react';
import { securityLogger } from '../utils/securityLogger';
import { useSecurityLogSender } from './useSecurityLogSender';
import type {
  SecurityMonitorConfig,
  SuspiciousActivityReason,
} from '../types/security';

const DEVTOOLS_THRESHOLD = 120;
const DEVTOOLS_CHECK_INTERVAL = 3000;

const SUSPICIOUS_ACTIVITY_COOLDOWNS: Record<SuspiciousActivityReason, number> = {
  devtools_potentially_open: 30 * 60 * 1000,
  multiple_tabs: 10 * 60 * 1000,
  paste_in_sensitive_field: 5 * 60 * 1000,
  external_script_injection_attempt: 60 * 1000,
};

export function useSecurityMonitor(config: SecurityMonitorConfig = {}) {
  const {
    detectDevTools = true,
    detectMultipleWindows = true,
    detectCopyPaste = false,
  } = config;
  const { sendSecurityLog } = useSecurityLogSender();
  const isSecurityMonitoringEnabled = process.env.NODE_ENV === 'production';

  const lastLoggedAtRef = useRef<Partial<Record<SuspiciousActivityReason, number>>>({});
  const devToolsOpenRef = useRef(false);
  const multipleTabsActiveRef = useRef(false);

  const shouldLogSuspiciousActivity = useCallback((reason: SuspiciousActivityReason) => {
    const now = Date.now();
    const lastLoggedAt = lastLoggedAtRef.current[reason] ?? 0;
    const cooldown = SUSPICIOUS_ACTIVITY_COOLDOWNS[reason];

    if (now - lastLoggedAt < cooldown) {
      return false;
    }

    lastLoggedAtRef.current[reason] = now;
    return true;
  }, []);

  const logSuspiciousActivity = useCallback(
    (reason: SuspiciousActivityReason, details?: Record<string, unknown>) => {
      if (!shouldLogSuspiciousActivity(reason)) {
        return;
      }

      const entry = securityLogger.log('suspicious_activity', {
        reason,
        ...details,
      });

      void sendSecurityLog(entry);
    },
    [sendSecurityLog, shouldLogSuspiciousActivity]
  );

  // Detectar abertura do DevTools (heurística simples)
  useEffect(() => {
    if (!detectDevTools || !isSecurityMonitoringEnabled) return;

    const detectDevToolsOpen = () => {
      const widthDelta = Math.max(0, window.outerWidth - window.innerWidth);
      const heightDelta = Math.max(0, window.outerHeight - window.innerHeight);
      const widthThreshold = widthDelta > DEVTOOLS_THRESHOLD;
      const heightThreshold = heightDelta > DEVTOOLS_THRESHOLD;
      const isDevToolsOpen = widthThreshold || heightThreshold;

      if (isDevToolsOpen && !devToolsOpenRef.current) {
        devToolsOpenRef.current = true;
        logSuspiciousActivity('devtools_potentially_open', {
          widthDelta,
          heightDelta,
        });
        return;
      }

      if (!isDevToolsOpen) {
        devToolsOpenRef.current = false;
      }
    };

    detectDevToolsOpen();
    window.addEventListener('resize', detectDevToolsOpen);
    window.addEventListener('focus', detectDevToolsOpen);
    document.addEventListener('visibilitychange', detectDevToolsOpen);

    const interval = setInterval(detectDevToolsOpen, DEVTOOLS_CHECK_INTERVAL);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', detectDevToolsOpen);
      window.removeEventListener('focus', detectDevToolsOpen);
      document.removeEventListener('visibilitychange', detectDevToolsOpen);
    };
  }, [detectDevTools, isSecurityMonitoringEnabled, logSuspiciousActivity]);

  // Detectar múltiplas abas abertas
  useEffect(() => {
    if (!detectMultipleWindows || !isSecurityMonitoringEnabled) return;

    const channel = new BroadcastChannel('tab-counter');

    let tabCount = 1;
    channel.onmessage = (event: MessageEvent<'tab-opened' | 'tab-closed'>) => {
      if (event.data === 'tab-opened') {
        tabCount++;
      }

      if (event.data === 'tab-closed') {
        tabCount = Math.max(1, tabCount - 1);
      }

      if (tabCount > 3 && !multipleTabsActiveRef.current) {
        multipleTabsActiveRef.current = true;
        logSuspiciousActivity('multiple_tabs', { count: tabCount });
      }

      if (tabCount <= 3) {
        multipleTabsActiveRef.current = false;
      }
    };

    channel.postMessage('tab-opened');

    return () => {
      channel.postMessage('tab-closed');
      channel.close();
    };
  }, [detectMultipleWindows, isSecurityMonitoringEnabled, logSuspiciousActivity]);

  // Detectar copy/paste em campos sensíveis
  const handleSensitiveFieldPaste = useCallback(
    (e: ClipboardEvent) => {
      if (!detectCopyPaste) return;

      const target = e.target as HTMLElement;
      if (
        target.hasAttribute('data-sensitive') ||
        target.getAttribute('type') === 'password'
      ) {
        logSuspiciousActivity('paste_in_sensitive_field');
      }
    },
    [detectCopyPaste, logSuspiciousActivity]
  );

  useEffect(() => {
    if (!detectCopyPaste || !isSecurityMonitoringEnabled) return;

    document.addEventListener('paste', handleSensitiveFieldPaste);
    return () => document.removeEventListener('paste', handleSensitiveFieldPaste);
  }, [detectCopyPaste, isSecurityMonitoringEnabled, handleSensitiveFieldPaste]);

  // Detectar tentativas de injeção de script
  useEffect(() => {
    if (!isSecurityMonitoringEnabled) return;

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (
            node.nodeName === 'SCRIPT' &&
            !(node as HTMLScriptElement).src.includes(window.location.origin)
          ) {
            logSuspiciousActivity('external_script_injection_attempt');
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [isSecurityMonitoringEnabled, logSuspiciousActivity]);
}
